import {
    ensureValidDispatchToken,
    forceRefreshDispatchToken,
    isDispatchAuthError,
} from "./dispatchTokenManager";
import { notifyBugsnag } from "@/lib/bugsnag";
import { getStoredExternalUserProfile } from "@/services/webSessionStorage";

export class ApiError extends Error {
    status: number;
    statusText: string;
    url: string;
    bodyText?: string;

    constructor(args: {
        status: number;
        statusText: string;
        url: string;
        message: string;
        bodyText?: string;
    }) {
        super(args.message);
        this.name = "ApiError";
        this.status = args.status;
        this.statusText = args.statusText;
        this.url = args.url;
        this.bodyText = args.bodyText;

        Object.setPrototypeOf(this, ApiError.prototype);
    }
}

export function isApiError(err: unknown): err is ApiError {
    return err instanceof ApiError;
}

export abstract class BaseService {
    protected abstract get baseUrl(): string;
    private static readonly TRANSIENT_STATUSES = new Set([
        408, 429, 500, 502, 503, 504,
    ]);

    private async sleep(ms: number): Promise<void> {
        await new Promise((resolve) => setTimeout(resolve, ms));
    }

    private getUserContext() {
        if (typeof window === "undefined") return undefined;

        try {
            const parsed = getStoredExternalUserProfile<{
                userId?: number;
                email?: string;
                roles?: string[];
                role?: string;
            }>();
            if (!parsed) return undefined;
            return {
                userId: parsed.userId,
                email: parsed.email,
                roles:
                    parsed.roles || (parsed.role ? [parsed.role] : undefined),
            };
        } catch {
            return undefined;
        }
    }

    protected async getValidToken(): Promise<string> {
        return ensureValidDispatchToken();
    }

    protected async request<T, D = unknown>(
        method: string,
        path: string,
        data?: D,
        responseType: "json" | "text" = "json",
        optionsOrRequireAuth:
            | boolean
            | {
                  requireAuth?: boolean;
                  retryable?: boolean;
                  retryCount?: number;
                  retryDelayMs?: number;
                  signal?: AbortSignal;
              } = {}
    ): Promise<T> {
        const options =
            typeof optionsOrRequireAuth === "boolean"
                ? { requireAuth: optionsOrRequireAuth }
                : optionsOrRequireAuth;

        const {
            requireAuth = true,
            retryable = method.toUpperCase() === "GET",
            retryCount = 1,
            retryDelayMs = 250,
            signal,
        }: {
            requireAuth?: boolean;
            retryable?: boolean;
            retryCount?: number;
            retryDelayMs?: number;
            signal?: AbortSignal;
        } = options;

        const url = `${this.baseUrl.replace(/\/$/, "")}/${path.replace(/^\/+/, "")}`;
        let token = requireAuth ? await this.getValidToken() : null;

        const createRequestInit = (
            bearerToken: string | null
        ): RequestInit => ({
            method,
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                ...(bearerToken
                    ? { Authorization: `Bearer ${bearerToken}` }
                    : {}),
            },
            body: data ? JSON.stringify(data) : undefined,
            ...(signal ? { signal } : {}),
        });

        let res: Response | null = null;
        let lastFetchError: unknown = null;
        const maxAttempts = retryable ? retryCount + 1 : 1;
        let hasRetriedAuth = false;

        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                res = await fetch(url, createRequestInit(token));
            } catch (error) {
                lastFetchError = error;
                if (attempt < maxAttempts) {
                    await this.sleep(retryDelayMs * attempt);
                    continue;
                }

                notifyBugsnag(error, {
                    kind: "fetch-failed",
                    method,
                    url,
                    metadata: { message: (error as Error)?.message },
                    user: this.getUserContext(),
                });
                throw error;
            }

            if (
                requireAuth &&
                !hasRetriedAuth &&
                (res.status === 401 || res.status === 403)
            ) {
                try {
                    token = await forceRefreshDispatchToken();
                    hasRetriedAuth = true;
                    res = null;
                    continue;
                } catch (refreshError) {
                    if (isDispatchAuthError(refreshError)) {
                        throw refreshError;
                    }
                    lastFetchError = refreshError;
                }
            }

            if (!res) {
                continue;
            }

            if (
                res.ok ||
                !retryable ||
                !BaseService.TRANSIENT_STATUSES.has(res.status) ||
                attempt >= maxAttempts
            ) {
                break;
            }

            await this.sleep(retryDelayMs * attempt);
        }

        if (!res) {
            throw lastFetchError instanceof Error
                ? lastFetchError
                : new Error("Request failed before response was received.");
        }

        // Read text once (safe even for json) so we can use it for errors or empty-body cases
        const bodyText = await res.text().catch(() => "");

        const correlationId =
            res.headers.get("x-correlation-id") ||
            res.headers.get("x-request-id") ||
            res.headers.get("traceparent") ||
            undefined;

        if (!res.ok) {
            const msg =
                bodyText?.trim() ||
                `Request failed (${res.status} ${res.statusText})`;

            notifyBugsnag(new Error("api-request-failed"), {
                kind: "api-request-failed",
                method,
                url,
                status: res.status,
                statusText: res.statusText,
                correlationId,
                bodySample: bodyText?.slice(0, 1000),
                user: this.getUserContext(),
            });

            throw new ApiError({
                status: res.status,
                statusText: res.statusText,
                url,
                message: msg,
                bodyText,
            });
        }

        if (res.status === 204) return undefined as unknown as T;

        if (responseType === "text") {
            return bodyText as unknown as T;
        }

        // JSON response
        if (!bodyText || bodyText.trim() === "") {
            // Some endpoints incorrectly return 200 with empty body
            return {} as T;
        }

        try {
            return JSON.parse(bodyText) as T;
        } catch (error) {
            notifyBugsnag(error, {
                kind: "json-parse-failed",
                method,
                url,
                status: res.status,
                correlationId,
                bodySample: bodyText?.slice(0, 1000),
                user: this.getUserContext(),
            });
            // If server returned non-JSON unexpectedly
            return bodyText as unknown as T;
        }
    }

    protected get<T>(path: string, requireAuth = true): Promise<T> {
        return this.request<T, undefined>("GET", path, undefined, "json", {
            requireAuth,
        });
    }

    protected getWithRetry<T>(path: string, retryCount = 1): Promise<T> {
        return this.request<T, undefined>("GET", path, undefined, "json", {
            retryable: true,
            retryCount,
        });
    }

    protected post<T, D = unknown>(
        path: string,
        data?: D,
        responseType: "json" | "text" = "json",
        requireAuth = true
    ): Promise<T> {
        return this.request<T, D>("POST", path, data, responseType, {
            requireAuth,
        });
    }

    protected postWithSignal<T, D = unknown>(
        path: string,
        data?: D,
        signal?: AbortSignal
    ): Promise<T> {
        return this.request<T, D>("POST", path, data, "json", {
            requireAuth: true,
            signal,
        });
    }

    protected postRetryable<T, D = unknown>(
        path: string,
        data?: D,
        responseType: "json" | "text" = "json",
        retryCount = 1
    ): Promise<T> {
        return this.request<T, D>("POST", path, data, responseType, {
            retryable: true,
            retryCount,
        });
    }

    protected put<T, D = unknown>(
        path: string,
        data?: D,
        responseType: "json" | "text" = "json",
        requireAuth = true
    ): Promise<T> {
        return this.request<T, D>("PUT", path, data, responseType, {
            requireAuth,
        });
    }

    protected delete<T>(path: string, requireAuth = true): Promise<T> {
        return this.request<T, undefined>("DELETE", path, undefined, "json", {
            requireAuth,
        });
    }

    protected deleteWithBody<T, D = unknown>(
        path: string,
        data?: D,
        requireAuth = true
    ): Promise<T> {
        return this.request<T, D>("DELETE", path, data, "json", {
            requireAuth,
        });
    }
}
